import { useEffect, useRef, useState } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import {
  AuthFactoryProvider,
  LoadingOverlay,
  hasOAuthCallbackParams,
  useAuth,
} from '@spotify/confidence-core-react';
import {
  TrackingScope,
  withPageImpression,
} from '@spotify/plugin-confidence-tracking-react';

import { AccountCreated } from './AccountCreated';
import { CreateAccountForm } from './CreateAccountForm';
import { InviteError } from './InviteError';
import {
  CreatedAccount,
  markAuthRedirectReturn,
  useInvite,
  useRegionCheck,
} from './hooks';
import { getSignupReturnPath } from './signupUtils';

const CLIENT_ID = '82qMvwZvqd3t3S0gRDvs8R53TehQXSJY';
const handleSignupTokenReceived = () => undefined;

const SignUpContent = ({
  inviteId,
  hasAuthCallback,
}: {
  inviteId?: string;
  hasAuthCallback: boolean;
}) => {
  const navigate = useNavigate();
  const [createdAccount, setCreatedAccount] = useState<CreatedAccount>();
  const [createdAccountInviteId, setCreatedAccountInviteId] =
    useState(inviteId);

  if (createdAccountInviteId !== inviteId) {
    setCreatedAccountInviteId(inviteId);
    setCreatedAccount(undefined);
  }

  const [{ error: inviteError, loading, value: inviteData }, getAccountInvite] =
    useInvite(inviteId);

  const account = createdAccount ?? inviteData?.createdAccount;

  const {
    loading: regionLoading,
    error: regionError,
    regionData,
  } = useRegionCheck();

  useEffect(() => {
    const abortController = new AbortController();
    if (inviteId) {
      void getAccountInvite(abortController.signal);
    }
    return () => abortController.abort();
  }, [getAccountInvite, inviteId]);

  useEffect(() => {
    if (regionError) {
      // eslint-disable-next-line no-console
      console.warn(
        'Region check failed, allowing signup to proceed:',
        regionError,
      );
      return;
    }

    if (!regionLoading && regionData?.allowed === false) {
      navigate('/signup/unsupported-market');
    }
  }, [regionLoading, regionError, regionData, navigate]);

  if (inviteError) {
    return <InviteError error={inviteError} />;
  }
  if (account) {
    return <AccountCreated createdAccount={account} />;
  }

  return (
    <AuthFactoryProvider
      clientId={CLIENT_ID}
      mode="signup"
      redirectUri={`${window.location.origin}/signup`}
      onTokenReceived={handleSignupTokenReceived}
    >
      {loading || regionLoading ? (
        <LoadingOverlay loading />
      ) : (
        <TrackingScope elementId="main.signup">
          <CreateAccountForm
            invite={inviteData}
            inviteId={inviteId}
            hasAuthCallback={hasAuthCallback}
            onCreate={setCreatedAccount}
          />
        </TrackingScope>
      )}
    </AuthFactoryProvider>
  );
};

const SignUpPageComponent = () => {
  const { id: inviteId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading: appAuthLoading } = useAuth();

  const signupReturnPath = getSignupReturnPath({
    returnTo: searchParams.get('signupReturnTo'),
    origin: window.location.origin,
  });
  const hasSignupAuthCallbackParams = hasOAuthCallbackParams(searchParams);
  const [isSignupAuthCallback, setIsSignupAuthCallback] = useState(
    hasSignupAuthCallbackParams,
  );
  const hasMarkedAuthRedirectReturnRef = useRef(false);
  if (hasSignupAuthCallbackParams && !hasMarkedAuthRedirectReturnRef.current) {
    hasMarkedAuthRedirectReturnRef.current = true;
    markAuthRedirectReturn();
  }
  if (hasSignupAuthCallbackParams && !isSignupAuthCallback) {
    setIsSignupAuthCallback(true);
  }

  if (
    signupReturnPath &&
    signupReturnPath !==
      `${location.pathname}${location.search}${location.hash}`
  ) {
    return <Navigate to={signupReturnPath} replace  data-track-source-anchor="999d4732d4;sign-up-page-component-navigate"/>;
  }

  if (!inviteId && !isSignupAuthCallback && appAuthLoading) {
    return <LoadingOverlay loading />;
  }

  if (!inviteId && !isSignupAuthCallback && isAuthenticated) {
    return <Navigate to="/" replace  data-track-source-anchor="aa2270db8b;sign-up-page-component-navigate-2"/>;
  }

  return (
    <SignUpContent inviteId={inviteId} hasAuthCallback={isSignupAuthCallback} />
  );
};

export const SignUpPage = withPageImpression(SignUpPageComponent, {
  pageId: 'signup',
});
